import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { usersApi } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import useAppStore from '@/stores/appStore';
import { 
  Users as UsersIcon, 
  MagnifyingGlass, 
  Plus, 
  UserCircle,
  Envelope,
  Phone,
  Shield,
  Key,
  CheckCircle,
  XCircle,
  PencilSimple,
  Trash
} from '@phosphor-icons/react';

export const AdminUsers = () => {
  const currentUser = useAppStore((state) => state.currentUser);
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';
  const navigate = useNavigate();

  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [activeTab, setActiveTab] = useState(currentUser?.role === 'foc' ? 'ratings' : 'staff');
  
  // Staff Reviews State
  const [staffReviews, setStaffReviews] = useState([]);
  const [loadingRatings, setLoadingRatings] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false);
  
  // Creation Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('technician');
  const [password, setPassword] = useState('');
  const [submittingUser, setSubmittingUser] = useState(false);

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('technician');
  const [editPassword, setEditPassword] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [submittingEdit, setSubmittingEdit] = useState(false);

  // Delete User State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [submittingDelete, setSubmittingDelete] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedRole !== 'all') params.role = selectedRole;
      const { data } = await usersApi.list(params);
      setUsers(data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load team directory');
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    setLoadingRatings(true);
    try {
      const { data } = await usersApi.getStaffReviews();
      setStaffReviews(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load staff reviews');
    } finally {
      setLoadingRatings(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'ratings') {
      fetchRatings();
    } else {
      fetchUsers();
    }
  }, [activeTab, selectedRole]);

  const filteredStaffReviews = staffReviews.filter((staff) => {
    const query = searchQuery.toLowerCase();
    return (
      staff.full_name?.toLowerCase().includes(query) ||
      staff.phone?.includes(query) ||
      (staff.email && staff.email.toLowerCase().includes(query))
    );
  });

  const handleToggleStatus = async (user) => {
    const nextStatus = !user.is_active;
    try {
      await usersApi.update(user.id, { is_active: nextStatus });
      setUsers(users.map(u => u.id === user.id ? { ...u, is_active: nextStatus } : u));
      toast.success(`${user.full_name} has been ${nextStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Failed to toggle status:', error);
      toast.error('Failed to update account status');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!name || !phone || !password) {
      toast.error('Please fill out all required fields');
      return;
    }
    
    let trimmedPhone = phone.trim();
    if (/^\d{10}$/.test(trimmedPhone)) {
      trimmedPhone = `+91${trimmedPhone}`;
      setPhone(trimmedPhone);
    }
    if (!trimmedPhone.startsWith('+')) {
      toast.error('Phone number must start with "+" and include country code (e.g., +91)');
      return;
    }
    if (!/^\+[1-9]\d{1,14}$/.test(trimmedPhone)) {
      toast.error('Phone number must be in E.164 format (e.g., +919876543210)');
      return;
    }
    
    setSubmittingUser(true);
    try {
      const payload = {
        full_name: name,
        phone: trimmedPhone,
        password,
        role,
      };
      if (email) payload.email = email;
      
      const { data } = await usersApi.create(payload);
      setUsers([data, ...users]);
      toast.success('Staff user created successfully!');
      
      // Reset Form & Close
      setName('');
      setPhone('');
      setEmail('');
      setRole('technician');
      setPassword('');
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmittingUser(false);
    }
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setEditName(user.full_name || '');
    setEditPhone(user.phone || '');
    setEditEmail(user.email || '');
    setEditRole(user.role);
    setEditPassword('');
    setEditIsActive(user.is_active);
    setIsEditModalOpen(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    
    let trimmedPhone = editPhone.trim();
    if (/^\d{10}$/.test(trimmedPhone)) {
      trimmedPhone = `+91${trimmedPhone}`;
      setEditPhone(trimmedPhone);
    }
    if (!trimmedPhone.startsWith('+')) {
      toast.error('Phone number must start with "+" and include country code (e.g., +91)');
      return;
    }
    if (!/^\+[1-9]\d{1,14}$/.test(trimmedPhone)) {
      toast.error('Phone number must be in E.164 format (e.g., +919876543210)');
      return;
    }

    setSubmittingEdit(true);
    try {
      const payload = {};
      
      if (editName !== editUser.full_name) payload.full_name = editName;
      if (trimmedPhone !== editUser.phone) payload.phone = trimmedPhone;
      if (editEmail !== (editUser.email || '')) payload.email = editEmail || null;
      if (editRole !== editUser.role) payload.role = editRole;
      if (editIsActive !== editUser.is_active) payload.is_active = editIsActive;
      if (editPassword) payload.password = editPassword;
      
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setIsEditModalOpen(false);
        return;
      }
      
      const { data } = await usersApi.update(editUser.id, payload);
      setUsers(users.map(u => u.id === editUser.id ? { ...u, ...data } : u));
      toast.success(`${editName || editUser.full_name} updated successfully!`);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmittingEdit(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setSubmittingDelete(true);
    try {
      await usersApi.delete(userToDelete.id);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      toast.success(`${userToDelete.full_name || 'User'} has been deleted successfully`);
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSubmittingDelete(false);
    }
  };

  const filteredUsers = users.filter(user => {
    // Tab filter
    if (activeTab === 'staff') {
      if (user.role === 'customer') return false;
      // Role select filter within staff
      if (selectedRole !== 'all' && user.role !== selectedRole) return false;
    } else {
      if (user.role !== 'customer') return false;
    }

    // Search query filter
    const matchesSearch = 
      user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone?.includes(searchQuery) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'foc':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'agent':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'technician':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'foc': return 'Field Ops';
      case 'agent': return 'Support Agent';
      case 'technician': return 'Technician';
      case 'customer': return 'Customer';
      default: return role;
    }
  };

  return (
    <DashboardLayout role="super_admin">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black tracking-tight mb-2">Users Directory</h1>
            <p className="text-muted-foreground">Manage organization users, team members, and permissions</p>
          </div>
          {activeTab === 'staff' && (
            <Button 
              onClick={() => setIsAddModalOpen(true)}
              className="font-bold text-white hover:opacity-90 flex items-center gap-1.5 shadow"
              style={{ backgroundColor: brandColorAccent }}
            >
              <Plus size={18} weight="bold" /> Add Staff Member
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={(val) => {
          setActiveTab(val);
          setSelectedRole('all');
          setSearchQuery('');
        }} className="space-y-6">
          <TabsList className="bg-slate-100 p-1 border border-slate-200">
            {currentUser?.role !== 'foc' && (
              <>
                <TabsTrigger value="staff" className="font-bold text-xs uppercase tracking-wider px-5 py-2">
                  Staff Directory
                </TabsTrigger>
                <TabsTrigger value="customers" className="font-bold text-xs uppercase tracking-wider px-5 py-2">
                  Customers
                </TabsTrigger>
              </>
            )}
            <TabsTrigger value="ratings" className="font-bold text-xs uppercase tracking-wider px-5 py-2">
              Staff Reviews
            </TabsTrigger>
          </TabsList>

          {/* Filters */}
          <Card className="p-4 border flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 w-full">
              <MagnifyingGlass 
                size={20} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
              />
              <Input
                placeholder={activeTab === 'staff' ? "Search staff by name, email, or phone number..." : "Search customers by name, email, or phone number..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 border-slate-300"
              />
            </div>

            {activeTab === 'staff' && (
              <div className="w-full md:w-[240px]">
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="h-10 border-slate-300">
                    <SelectValue placeholder="Filter by Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff Roles</SelectItem>
                    <SelectItem value="technician">Technicians</SelectItem>
                    <SelectItem value="agent">Support Agents</SelectItem>
                    <SelectItem value="foc">Field Ops Coordinators</SelectItem>
                    <SelectItem value="super_admin">Super Admins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </Card>

          <TabsContent value="staff" className="mt-0">
            {loading ? (
              <LoadingSpinner />
            ) : filteredUsers.length > 0 ? (
              <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-xs font-black uppercase tracking-wider text-slate-500">
                        <th className="py-4 px-6">Name</th>
                        <th className="py-4 px-6">Phone</th>
                        <th className="py-4 px-6">Email</th>
                        <th className="py-4 px-6">Role</th>
                        <th className="py-4 px-6">Status</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-4 px-6 font-bold text-slate-800 flex items-center gap-2">
                            <UserCircle size={24} className="text-slate-400" />
                            {user.full_name}
                          </td>
                          <td className="py-4 px-6 font-medium text-slate-700">{user.phone}</td>
                          <td className="py-4 px-6 text-slate-500">{user.email || '—'}</td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getRoleBadgeColor(user.role)}`}>
                              {getRoleLabel(user.role)}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center gap-1 text-xs font-bold ${user.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {user.is_active ? (
                                <>
                                  <CheckCircle size={14} weight="fill" /> Active
                                </>
                              ) : (
                                <>
                                  <XCircle size={14} weight="fill" /> Deactivated
                                </>
                              )}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2 flex-nowrap">
                              <Button
                                onClick={() => openEditModal(user)}
                                variant="outline"
                                size="sm"
                                className="font-bold text-xs uppercase tracking-wider px-3 h-8 gap-1"
                              >
                                <PencilSimple size={14} /> Edit
                              </Button>
                              {user.role !== 'super_admin' && (
                                <>
                                  <Button
                                    onClick={() => handleToggleStatus(user)}
                                    variant={user.is_active ? 'outline' : 'default'}
                                    size="sm"
                                    className={`font-bold text-xs uppercase tracking-wider px-3 h-8 ${
                                      user.is_active ? 'text-red-600 border-red-200 hover:bg-red-50' : ''
                                    }`}
                                  >
                                    {user.is_active ? 'Deactivate' : 'Activate'}
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setUserToDelete(user);
                                      setIsDeleteModalOpen(true);
                                    }}
                                    variant="destructive"
                                    size="sm"
                                    className="font-bold text-xs uppercase tracking-wider px-3 h-8 gap-1"
                                  >
                                    <Trash size={14} /> Delete
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <Card className="p-12 text-center border">
                <UsersIcon size={48} className="mx-auto text-slate-300 mb-4" />
                <h2 className="text-xl font-bold mb-2">No staff members found</h2>
                <p className="text-muted-foreground">Adjust filters or search parameters to view team members.</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="customers" className="mt-0">
            {loading ? (
              <LoadingSpinner />
            ) : filteredUsers.length > 0 ? (
              <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-xs font-black uppercase tracking-wider text-slate-500">
                        <th className="py-4 px-6">Name</th>
                        <th className="py-4 px-6">Phone</th>
                        <th className="py-4 px-6">Email</th>
                        <th className="py-4 px-6">Status</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-4 px-6 font-bold text-slate-800 flex items-center gap-2">
                            <UserCircle size={24} className="text-slate-400" />
                            {user.full_name}
                          </td>
                          <td className="py-4 px-6 font-medium text-slate-700">{user.phone}</td>
                          <td className="py-4 px-6 text-slate-500">{user.email || '—'}</td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center gap-1 text-xs font-bold ${user.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {user.is_active ? (
                                <>
                                  <CheckCircle size={14} weight="fill" /> Active
                                </>
                              ) : (
                                <>
                                  <XCircle size={14} weight="fill" /> Deactivated
                                </>
                              )}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2 flex-nowrap">
                              <Button
                                onClick={() => openEditModal(user)}
                                variant="outline"
                                size="sm"
                                className="font-bold text-xs uppercase tracking-wider px-3 h-8 gap-1"
                              >
                                <PencilSimple size={14} /> Edit
                              </Button>
                              <Button
                                onClick={() => handleToggleStatus(user)}
                                variant={user.is_active ? 'outline' : 'default'}
                                size="sm"
                                className={`font-bold text-xs uppercase tracking-wider px-3 h-8 ${
                                  user.is_active ? 'text-red-600 border-red-200 hover:bg-red-50' : ''
                                }`}
                              >
                                {user.is_active ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button
                                onClick={() => {
                                  setUserToDelete(user);
                                  setIsDeleteModalOpen(true);
                                }}
                                variant="destructive"
                                size="sm"
                                className="font-bold text-xs uppercase tracking-wider px-3 h-8 gap-1"
                              >
                                <Trash size={14} /> Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <Card className="p-12 text-center border">
                <UsersIcon size={48} className="mx-auto text-slate-300 mb-4" />
                <h2 className="text-xl font-bold mb-2">No customers found</h2>
                <p className="text-muted-foreground">Adjust filters or search parameters to view customer directory.</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="ratings" className="mt-0">
            {loadingRatings ? (
              <LoadingSpinner />
            ) : filteredStaffReviews.length > 0 ? (
              <Card className="border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left text-xs">
                    <thead>
                      <tr className="border-b bg-slate-50 font-bold text-slate-400 uppercase tracking-wider">
                        <th className="p-4">Staff Name</th>
                        <th className="p-4">Role</th>
                        <th className="p-4">Phone & Email</th>
                        <th className="p-4">Status</th>
                        <th className="p-4 text-center">Average Rating</th>
                        <th className="p-4 text-center">Total Ratings</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y font-medium text-slate-700">
                      {filteredStaffReviews.map((staff) => (
                        <tr key={staff.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="p-4">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center font-bold text-indigo-700 uppercase">
                                {staff.full_name?.charAt(0)}
                              </div>
                              <span className="font-bold text-slate-900">{staff.full_name}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${
                              staff.role === 'technician' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'
                            }`}>
                              {staff.role === 'technician' ? 'Technician' : 'Support Agent'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div>{staff.phone}</div>
                            {staff.email && <div className="text-[10px] text-slate-400 mt-0.5">{staff.email}</div>}
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                              staff.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
                            }`}>
                              {staff.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            {staff.rating_avg !== null ? (
                              <div className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-black">
                                <span>★</span>
                                <span>{staff.rating_avg.toFixed(1)}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400 italic">No ratings</span>
                            )}
                          </td>
                          <td className="p-4 text-center font-bold">{staff.rating_count}</td>
                          <td className="p-4 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const params = new URLSearchParams({
                                  staff_id: staff.id,
                                  staff_name: staff.full_name,
                                  staff_role: staff.role,
                                });
                                navigate(`/ops/admin/tickets?${params.toString()}`);
                              }}
                              className="font-bold text-[10px] uppercase tracking-wider h-8"
                              disabled={staff.rating_count === 0}
                            >
                              View Tickets
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <Card className="p-12 text-center border">
                <UsersIcon size={40} className="mx-auto mb-3 text-slate-300" />
                <h3 className="font-bold text-slate-700">No Staff Reviews Found</h3>
                <p className="text-xs text-slate-400 mt-1">Submit rating reviews on ticket completion to see them here.</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Add User Modal */}
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Add Staff Member</DialogTitle>
              <DialogDescription className="text-xs">
                Create new credentials for coordinate support desk agents, dispatchers, or technicians.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              {/* Full Name */}
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Full Name
                </Label>
                <div className="relative">
                  <UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-11 border-slate-300"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Phone Number
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="919876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-10 h-11 border-slate-300 font-mono"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Email Address (Optional)
                </Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 border-slate-300"
                  />
                </div>
              </div>

              {/* Role */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Operations Role
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={role === 'technician' ? 'default' : 'outline'}
                    onClick={() => setRole('technician')}
                    className="flex-1 text-xs font-bold"
                  >
                    Technician
                  </Button>
                  <Button
                    type="button"
                    variant={role === 'agent' ? 'default' : 'outline'}
                    onClick={() => setRole('agent')}
                    className="flex-1 text-xs font-bold"
                  >
                    Agent
                  </Button>
                  <Button
                    type="button"
                    variant={role === 'foc' ? 'default' : 'outline'}
                    onClick={() => setRole('foc')}
                    className="flex-1 text-xs font-bold"
                  >
                    Field Ops
                  </Button>
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <Label htmlFor="pass" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Staff Password
                </Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="pass"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11 border-slate-300 font-mono"
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsAddModalOpen(false)}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submittingUser}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                  style={{ backgroundColor: brandColorAccent }}
                >
                  {submittingUser ? 'Saving...' : 'Add Team Member'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Edit User</DialogTitle>
              <DialogDescription className="text-xs">
                Update user details. Leave password blank to keep the current one.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleEditUser} className="space-y-4 mt-4">
              {/* Full Name */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-name" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Full Name
                </Label>
                <div className="relative">
                  <UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="edit-name"
                    type="text"
                    placeholder="Enter full name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="pl-10 h-11 border-slate-300"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-phone" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Phone Number
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="edit-phone"
                    type="tel"
                    placeholder="919876543210"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    className="pl-10 h-11 border-slate-300 font-mono"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-email" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Email Address
                </Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="edit-email"
                    type="email"
                    placeholder="name@company.com"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="pl-10 h-11 border-slate-300"
                  />
                </div>
              </div>

              {/* Role */}
              {editUser?.role !== 'super_admin' && editUser?.role !== 'customer' && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Operations Role
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={editRole === 'technician' ? 'default' : 'outline'}
                      onClick={() => setEditRole('technician')}
                      className="flex-1 text-xs font-bold"
                    >
                      Technician
                    </Button>
                    <Button
                      type="button"
                      variant={editRole === 'agent' ? 'default' : 'outline'}
                      onClick={() => setEditRole('agent')}
                      className="flex-1 text-xs font-bold"
                    >
                      Agent
                    </Button>
                    <Button
                      type="button"
                      variant={editRole === 'foc' ? 'default' : 'outline'}
                      onClick={() => setEditRole('foc')}
                      className="flex-1 text-xs font-bold"
                    >
                      Field Ops
                    </Button>
                  </div>
                </div>
              )}

              {/* Password */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-pass" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  New Password <span className="text-slate-400 normal-case font-normal">(leave blank to keep current)</span>
                </Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="edit-pass"
                    type="password"
                    placeholder="••••••••"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                    className="pl-10 h-11 border-slate-300 font-mono"
                  />
                </div>
              </div>

              {/* Active Status */}
              {editUser?.role !== 'super_admin' && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Account Status
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={editIsActive ? 'default' : 'outline'}
                      onClick={() => setEditIsActive(true)}
                      className={`flex-1 text-xs font-bold gap-1 ${editIsActive ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                    >
                      <CheckCircle size={14} /> Active
                    </Button>
                    <Button
                      type="button"
                      variant={!editIsActive ? 'default' : 'outline'}
                      onClick={() => setEditIsActive(false)}
                      className={`flex-1 text-xs font-bold gap-1 ${!editIsActive ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    >
                      <XCircle size={14} /> Deactivated
                    </Button>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsEditModalOpen(false)}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submittingEdit}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                  style={{ backgroundColor: brandColorAccent }}
                >
                  {submittingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detailed Reviews Modal */}
        <Dialog open={isReviewsModalOpen} onOpenChange={setIsReviewsModalOpen}>
          <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Reviews for {selectedStaff?.full_name}</DialogTitle>
              <DialogDescription className="text-xs">
                Showing rating history and feedback comments from customers
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-3 py-3 pr-1">
              {selectedStaff?.ratings && selectedStaff.ratings.length > 0 ? (
                selectedStaff.ratings.map((review) => (
                  <div key={review.id} className="p-3 bg-slate-50 border border-slate-100 rounded-lg space-y-1.5">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-indigo-700">Ticket {review.ticket_number}</span>
                      <span className="text-[10px] text-slate-400">
                        {review.created_at ? new Date(review.created_at).toLocaleDateString() : '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <span key={s} className={s <= review.stars ? 'text-amber-500 text-sm' : 'text-slate-200 text-sm'}>
                          ★
                        </span>
                      ))}
                    </div>
                    {review.comment && review.comment !== 'None' ? (
                      <p className="text-xs text-slate-700 italic font-semibold">"{review.comment}"</p>
                    ) : (
                      <p className="text-[10px] text-slate-400 italic">No comment feedback left</p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-500 italic text-center py-4">No reviews recorded yet.</p>
              )}
            </div>
            <DialogFooter className="mt-2">
              <Button size="sm" onClick={() => setIsReviewsModalOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black text-red-600">Delete User</DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-2">
                Are you sure you want to delete <strong>{userToDelete?.full_name}</strong>? This action cannot be undone. All ticket history and association with existing entities will be preserved, but the user will no longer be able to access the application.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setUserToDelete(null);
                }}
                className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={submittingDelete}
                onClick={handleDeleteUser}
                className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white bg-red-600 hover:bg-red-700"
              >
                {submittingDelete ? 'Deleting...' : 'Delete User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
